/**
 * Canonical outbound approval decision contract — the wire shape the Phase 3 agent
 * publishes to the voll-product Kafka topics `policy.approval.success.registered`
 * and `policy.approval.denied.registered`.
 *
 * Consumer: the foreign `policy-business-api` success/denied registered streams.
 *
 * ⚠ voucherId rides in BOTH the KEY and the VALUE — the consumer is inconsistent:
 * the success/denied registered streams read it from the KEY (`const { voucherId }
 * = key`), but the manager/auto-conclude branch reads it from the VALUE
 * (`insertApprovalManagerService` → `const { userProfile, voucherId } = value`).
 * Omitting it from the value silently breaks the MANAGER path: the approval is
 * inserted with `voucherId: undefined`, then `policy.approval.success.concluded`'s
 * `findByVoucherIdService(<real voucherId from key>)` finds nothing → logs
 * "Approvals não encontrado" → never emits `voucher.approved` → the voucher is
 * stuck "aguardando". (Non-manager approvers take the branch that reads the KEY,
 * so they were unaffected — which masked the bug.) So the publisher MUST set
 * `voucherId` in the KEY *and* the VALUE. Both success and denied events share the
 * same key + value shape (`justification` is the meaningful denial reason but is
 * structurally optional), so one key schema and one value schema serve both.
 *
 * This is a cross-service WIRE contract, NOT a persisted Mongo document, so it
 * carries no `_schemaVersion`.
 *
 * `date` is a raw ISO-8601 UTC string carried through untouched — never
 * `z.date()` / `z.coerce.date()`. The consumer wraps it with `new Date(date)`, so
 * an ISO UTC string is correct; do NOT pre-shift (voucher-date-fidelity landmine).
 *
 * Foreign field name `userProfile` is kept verbatim to match the bus.
 */
import { z } from 'zod';

export const ApprovalDecisionKeySchema = z.object({
  voucherId: z.string(),
  correlationId: z.string().optional(),
  voucherCode: z.string().optional(),
});
export type ApprovalDecisionKey = z.infer<typeof ApprovalDecisionKeySchema>;

export const ApprovalDecisionValueSchema = z.object({
  // Mirrors the KEY's voucherId — the manager/auto-conclude consumer branch
  // (`insertApprovalManagerService`) reads it from the VALUE, not the key.
  voucherId: z.string(),
  userId: z.number(),
  userProfile: z.number(),
  name: z.string(),
  email: z.string(),
  date: z.string(),
  justification: z.string().optional(),
  externalCode: z.string().optional(),
  modal: z.string().optional(),
});
export type ApprovalDecisionValue = z.infer<typeof ApprovalDecisionValueSchema>;
